import * as React from 'react'
import {
  ChevronDown, ChevronRight, Copy, EyeOff, GripVertical, Plus, Trash2,
} from 'lucide-react'
import type { FieldSpec, RuleCondition, RuleGroup, RuleNode } from '@/lib/types'
import { isGroup } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { DateTimePicker } from '@/components/ui/date-picker'
import { Checkbox } from '@/components/ui/misc'
import { Tooltip } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

/** Structural limits mirrored from the engine (§6.6). */
export const MAX_DEPTH = 5
export const MAX_NODES = 100

/** Condition class badges (§5.6 条件类型徽标). */
export const CLASS_META: Record<
  FieldSpec['class'],
  { label: string; short: string; variant: 'default' | 'secondary' | 'warning' | 'muted'; hint: string }
> = {
  generation_constraint: {
    label: '生成约束',
    short: '缩小候选空间，最省成本',
    variant: 'default',
    hint: '可前置到候选生成器，直接缩小候选空间，执行成本最低',
  },
  local_match: {
    label: '本地匹配',
    short: '本地字符串匹配，不查外部',
    variant: 'secondary',
    hint: '在本地对每个候选求值，不产生外部查询',
  },
  indexable: {
    label: '可索引',
    short: '查询现有域名库时走索引',
    variant: 'secondary',
    hint: '查询现有域名库时可下推到数据库索引',
  },
  computed_feature: {
    label: '计算特征',
    short: '由域名本身算出，可随时重算',
    variant: 'secondary',
    hint: '纯函数计算，可随时重算',
  },
  external_state: {
    label: '外部状态',
    short: '需外部确认，消耗查询额度',
    variant: 'warning',
    // §6.2.1 is the whole reason this badge exists: the user has to see, at
    // authoring time, which conditions will cost quota and delay their hits.
    hint: '需要外部确认：命中会先进入“待确认”，并消耗 external_query 额度',
  },
}

/** Orders the dropdown cheapest-first, which is also the order a user should
 *  think in: narrow the space before spending an external lookup. */
const CLASS_ORDER: FieldSpec['class'][] = [
  'generation_constraint', 'local_match', 'computed_feature', 'indexable', 'external_state',
]

function groupFieldsByClass(fields: FieldSpec[]) {
  return CLASS_ORDER
    .map((cls) => ({ cls, fields: fields.filter((f) => f.class === cls) }))
    .filter((g) => g.fields.length > 0)
}

export interface TreeContext {
  fields: FieldSpec[]
  errors: Record<string, string>
  onChange: (path: string, updater: (node: RuleNode) => RuleNode | null) => void
  onAddCondition: (path: string, fieldName?: string) => void
  onAddGroup: (path: string) => void
  onDuplicate: (path: string) => void
  nodeCount: number
  /** Exact main-label length, when an active AND constraint fixes it. */
  labelLength?: number
}

/** Renders one node of the rule tree. */
export function RuleNodeEditor({
  node,
  path,
  depth,
  ctx,
  index,
  siblingCount,
  onMove,
}: {
  node: RuleNode
  path: string
  depth: number
  ctx: TreeContext
  index: number
  siblingCount: number
  onMove?: (from: number, to: number) => void
}) {
  if (isGroup(node)) {
    return (
      <GroupEditor
        group={node} path={path} depth={depth} ctx={ctx}
        index={index} siblingCount={siblingCount} onMove={onMove}
      />
    )
  }
  return (
    <ConditionEditor
      condition={node} path={path} ctx={ctx}
      index={index} siblingCount={siblingCount} onMove={onMove}
    />
  )
}

function GroupEditor({
  group, path, depth, ctx, index, siblingCount, onMove,
}: {
  group: RuleGroup
  path: string
  depth: number
  ctx: TreeContext
  index: number
  siblingCount: number
  onMove?: (from: number, to: number) => void
}) {
  const [collapsed, setCollapsed] = React.useState(false)
  const isRoot = path === '$.root'
  const atDepthLimit = depth >= MAX_DEPTH
  const atNodeLimit = ctx.nodeCount >= MAX_NODES
  const error = ctx.errors[path] || ctx.errors[`${path}.children`] || ctx.errors[`${path}.op`]

  function update(patch: Partial<RuleGroup>) {
    ctx.onChange(path, (n) => ({ ...(n as RuleGroup), ...patch }))
  }

  function moveChild(from: number, to: number) {
    if (to < 0 || to >= group.children.length) return
    const children = [...group.children]
    const [moved] = children.splice(from, 1)
    children.splice(to, 0, moved)
    update({ children })
  }

  return (
    <div
      className={cn(
        'rounded-lg border-2 bg-card/60',
        group.disabled && 'opacity-50',
        error ? 'border-destructive' : group.not ? 'border-destructive/40' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-3 py-2">
        <Button
          variant="ghost" size="icon-sm"
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? '展开分组' : '折叠分组'}
          aria-expanded={!collapsed}
        >
          {collapsed ? <ChevronRight className="h-4 w-4" aria-hidden /> : <ChevronDown className="h-4 w-4" aria-hidden />}
        </Button>

        {/* AND/OR toggle */}
        <div className="inline-flex rounded-md border" role="group" aria-label="逻辑运算符">
          {(['AND', 'OR'] as const).map((op) => (
            <button
              key={op}
              type="button"
              onClick={() => update({ op })}
              aria-pressed={group.op === op}
              className={cn(
                'px-2.5 py-1 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                group.op === op ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
              )}
            >
              {op === 'AND' ? '全部满足 AND' : '任一满足 OR'}
            </button>
          ))}
        </div>

        <Tooltip content="NOT：对整个分组的结果取反">
          <label className="flex items-center gap-1.5 text-xs">
            <Checkbox
              checked={Boolean(group.not)}
              onCheckedChange={(v) => update({ not: Boolean(v) })}
              aria-label="对该分组取反"
            />
            NOT
          </label>
        </Tooltip>

        <Badge variant="muted" className="text-[10px]">
          {group.children.length} 项 · 深度 {depth}
        </Badge>

        <div className="ml-auto flex items-center gap-1">
          {siblingCount > 1 && onMove && (
            <>
              <Button
                variant="ghost" size="icon-sm" onClick={() => onMove(index, index - 1)}
                disabled={index === 0} aria-label="上移分组"
              >
                <span aria-hidden>↑</span>
              </Button>
              <Button
                variant="ghost" size="icon-sm" onClick={() => onMove(index, index + 1)}
                disabled={index === siblingCount - 1} aria-label="下移分组"
              >
                <span aria-hidden>↓</span>
              </Button>
            </>
          )}
          <Tooltip content={group.disabled ? '启用分组' : '停用分组（保留但不参与匹配）'}>
            <Button
              variant="ghost" size="icon-sm"
              onClick={() => update({ disabled: !group.disabled })}
              aria-label={group.disabled ? '启用分组' : '停用分组'}
            >
              <EyeOff className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tooltip>
          {!isRoot && (
            <Tooltip content="删除分组">
              <Button
                variant="ghost" size="icon-sm"
                onClick={() => ctx.onChange(path, () => null)}
                aria-label="删除分组"
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="border-b bg-destructive/5 px-3 py-1.5 text-xs text-destructive">
          {error}
        </p>
      )}

      {!collapsed && (
        <div className="space-y-2 p-3">
          {group.children.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
              空分组无法保存，请至少添加一个条件
            </p>
          ) : (
            group.children.map((child, i) => (
              <RuleNodeEditor
                key={child.id ?? `${path}.children[${i}]`}
                node={child}
                path={`${path}.children[${i}]`}
                depth={depth + 1}
                ctx={ctx}
                index={i}
                siblingCount={group.children.length}
                onMove={moveChild}
              />
            ))
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              variant="outline" size="sm"
              onClick={() => ctx.onAddCondition(path)}
              disabled={atNodeLimit}
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> 新增条件
            </Button>
            <Tooltip
              content={
                atDepthLimit
                  ? `已达到最大嵌套深度 ${MAX_DEPTH}`
                  : atNodeLimit
                    ? `已达到最大条件数 ${MAX_NODES}`
                    : '新增一个嵌套分组'
              }
            >
              <span>
                <Button
                  variant="outline" size="sm"
                  onClick={() => ctx.onAddGroup(path)}
                  disabled={atDepthLimit || atNodeLimit}
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden /> 新增嵌套分组
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  )
}

function ConditionEditor({
  condition, path, ctx, index, siblingCount, onMove,
}: {
  condition: RuleCondition
  path: string
  ctx: TreeContext
  index: number
  siblingCount: number
  onMove?: (from: number, to: number) => void
}) {
  const spec = ctx.fields.find((f) => f.name === condition.field)
  const operators = spec ? Object.keys(spec.operators).sort() : []
  const valueKind = spec?.operators[condition.operator]
  const error = findNodeError(ctx.errors, path)
  const classMeta = spec ? CLASS_META[spec.class] : null

  function update(patch: Partial<RuleCondition>) {
    ctx.onChange(path, (n) => ({ ...(n as RuleCondition), ...patch }))
  }

  function onFieldChange(fieldName: string) {
    const next = ctx.fields.find((f) => f.name === fieldName)
    const firstOp = preferredOperator(next)
    // Changing the field invalidates the operator and value, so both reset to
    // a valid default rather than leaving an impossible combination behind.
    update({ field: fieldName, operator: firstOp, value: defaultValueFor(next, firstOp) })
  }

  return (
    <div
      className={cn(
        'rounded-md border bg-background p-2.5',
        condition.disabled && 'opacity-50',
        error ? 'border-destructive' : condition.not ? 'border-destructive/40' : 'border-border',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />

        <Tooltip content="NOT：对该条件取反">
          <label className="flex items-center gap-1 text-[11px]">
            <Checkbox
              checked={Boolean(condition.not)}
              onCheckedChange={(v) => update({ not: Boolean(v) })}
              aria-label="对该条件取反"
            />
            NOT
          </label>
        </Tooltip>

        <select
          value={condition.field}
          onChange={(e) => onFieldChange(e.target.value)}
          className="h-8 max-w-[15rem] rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="字段"
          title={spec?.description}
        >
          <option value="">选择字段…</option>
          {groupFieldsByClass(ctx.fields).map(({ cls, fields }) => (
            <optgroup key={cls} label={CLASS_META[cls].label + ' — ' + CLASS_META[cls].short}>
              {fields.map((f) => (
                <option
                  key={f.name}
                  value={f.name}
                  // A deferred field would leave every candidate permanently
                  // undetermined, so it is visible but not selectable.
                  disabled={f.deferred}
                  title={f.deferred ? f.deferred_note : f.description}
                >
                  {f.label || f.name}
                  {f.deferred ? '（P1 未开放）' : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {operators.length > 1 && (
          <select
            value={condition.operator}
            onChange={(e) =>
              update({ operator: e.target.value, value: defaultValueFor(spec, e.target.value) })
            }
            disabled={!spec}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            aria-label="运算符"
          >
            {operators.map((op) => (
              <option key={op} value={op}>{operatorLabel(op)}</option>
            ))}
          </select>
        )}

        <ValueEditor
          kind={valueKind}
          spec={spec}
          value={condition.value}
          onChange={(value) => update({ value })}
          invalid={Boolean(ctx.errors[`${path}.value`])}
        />

        {classMeta && (
          <Tooltip content={classMeta.hint}>
            <Badge variant={classMeta.variant} className="shrink-0 text-[10px]">
              {classMeta.label}
            </Badge>
          </Tooltip>
        )}

        <div className="ml-auto flex items-center gap-0.5">
          {siblingCount > 1 && onMove && (
            <>
              <Button variant="ghost" size="icon-sm" onClick={() => onMove(index, index - 1)} disabled={index === 0} aria-label="上移条件">
                <span aria-hidden>↑</span>
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={() => onMove(index, index + 1)} disabled={index === siblingCount - 1} aria-label="下移条件">
                <span aria-hidden>↓</span>
              </Button>
            </>
          )}
          <Tooltip content="复制条件">
            <Button
              variant="ghost" size="icon-sm"
              onClick={() => ctx.onDuplicate(path)}
              aria-label="复制条件"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tooltip>
          <Tooltip content={condition.disabled ? '启用条件' : '停用条件'}>
            <Button
              variant="ghost" size="icon-sm"
              onClick={() => update({ disabled: !condition.disabled })}
              aria-label={condition.disabled ? '启用条件' : '停用条件'}
            >
              <EyeOff className="h-3.5 w-3.5" aria-hidden />
            </Button>
          </Tooltip>
          <Button
            variant="ghost" size="icon-sm"
            onClick={() => ctx.onChange(path, () => null)}
            aria-label="删除条件"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
          </Button>
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-1.5 pl-6 text-[11px] text-destructive">{error}</p>
      )}
    </div>
  )
}

/** Renders the right input for the operator's declared value kind. */
export function ValueEditor({
  kind, spec, value, onChange, invalid,
}: {
  kind?: string
  spec?: FieldSpec
  value: unknown
  onChange: (v: unknown) => void
  invalid?: boolean
}) {
  if (!kind) return null

  const common = 'h-8 text-xs'
  const visibleEnum = spec?.enum?.filter((option) => !option.hidden) ?? []

  switch (kind) {
    case 'bool':
      return (
        <select
          value={String(Boolean(value))}
          onChange={(e) => onChange(e.target.value === 'true')}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="值"
        >
          <option value="true">是</option>
          <option value="false">否</option>
        </select>
      )

    case 'string':
      if (visibleEnum.length) {
        const selected = spec?.enum?.find((o) => o.value === value)
        return (
          <span className="inline-flex items-center gap-1.5">
            <select
              value={String(value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              aria-invalid={invalid}
				className={cn(
					'max-w-[13rem] rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
					spec?.name === 'tld' ? 'h-9 min-w-44' : 'h-8',
				)}
              aria-label="值"
              title={selected?.hint}
            >
              <option value="">选择…</option>
              {selected?.hidden && <option value={selected.value}>{selected.label}（旧规则）</option>}
              {visibleEnum.map((o) => (
                <option key={o.value} value={o.value} title={o.hint}>
                  {o.label}
                </option>
              ))}
            </select>
            {selected?.hint && (
              <Tooltip content={selected.hint}>
                <span className="cursor-help text-[10px] text-muted-foreground">ⓘ</span>
              </Tooltip>
            )}
          </span>
        )
      }
      return (
        <Input
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid}
          className={cn(common, 'w-44')}
          placeholder="值"
          aria-label="值"
        />
      )

    case 'string_list': {
      const list = Array.isArray(value) ? (value as string[]) : []

      // With a known vocabulary, offer it: typing "pendingdelete" from memory
      // is exactly the error the canonicalisation has to forgive.
      if (visibleEnum.length) {
		if (spec?.name === 'tld') {
			return (
				<TldMultiSelect
					options={visibleEnum}
					value={list}
					onChange={onChange}
					invalid={invalid}
				/>
			)
		}
        return (
          <span className="inline-flex max-w-[22rem] flex-wrap items-center gap-1">
            {visibleEnum.map((o) => {
              const active = list.includes(o.value)
              return (
                <Tooltip key={o.value} content={o.hint}>
                  <button
                    type="button"
                    onClick={() =>
                      onChange(active ? list.filter((v) => v !== o.value) : [...list, o.value])
                    }
                    aria-pressed={active}
                    className={cn(
                      'rounded border px-1.5 py-0.5 text-[11px] transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      active
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'hover:bg-accent',
                    )}
                  >
                    {o.label}
                  </button>
                </Tooltip>
              )
            })}
          </span>
        )
      }

      return (
        <Input
          value={list.join(', ')}
          onChange={(e) =>
            onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))
          }
          aria-invalid={invalid}
          className={cn(common, 'w-56')}
          placeholder="逗号分隔，如 xyz, top"
          aria-label="值列表"
        />
      )
    }

    case 'int':
    case 'days':
      return (
        <Input
          type="number"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(numberOrBlank(e.target.value))}
		  onFocus={(e) => {
			  if (e.currentTarget.value === '0') e.currentTarget.select()
		  }}
          aria-invalid={invalid}
          className={cn(common, 'w-24')}
          aria-label="数值"
        />
      )

    case 'float':
      return (
        <Input
          type="number" step="0.1"
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => onChange(numberOrBlank(e.target.value))}
		  onFocus={(e) => {
			  if (e.currentTarget.value === '0') e.currentTarget.select()
		  }}
          aria-invalid={invalid}
          className={cn(common, 'w-24')}
          aria-label="数值"
        />
      )

    case 'int_range': {
      const range = Array.isArray(value) ? (value as Array<number | ''>) : [0, 0]
      return (
        <span className="inline-flex items-center gap-1">
          <Input
            type="number" value={range[0] ?? 0}
            onChange={(e) => onChange([numberOrBlank(e.target.value), range[1] ?? 0])}
            className={cn(common, 'w-20')} aria-label="最小值"
          />
          <span className="text-xs text-muted-foreground">–</span>
          <Input
            type="number" value={range[1] ?? 0}
            onChange={(e) => onChange([range[0] ?? 0, numberOrBlank(e.target.value)])}
            className={cn(common, 'w-20')} aria-label="最大值"
          />
        </span>
      )
    }

    case 'time':
      return (
        <DateTimePicker
          value={toLocalInput(value)}
          // Guard the empty case: clearing the field used to reach
          // new Date('').toISOString(), which throws RangeError rather than
          // yielding an empty condition.
          onChange={(v) => onChange(v ? new Date(v).toISOString() : '')}
          className={cn(common, 'w-52')}
          aria-label="时间（本地时区，提交为 UTC）"
        />
      )

    case 'repeat_options': {
      const opts = (value ?? {}) as {
        unit_length?: number | ''
        repeats?: number | ''
        min_repeats?: number | ''
        arrangement?: 'any' | 'block' | 'grouped'
        scope?: 'whole' | 'prefix' | 'suffix' | 'range'
        segment_start?: number | ''
        segment_length?: number | ''
      }
      const num = (v?: number | '') => (typeof v === 'number' && v > 0 ? String(v) : '')
      const exact = typeof opts.repeats === 'number' && opts.repeats > 0
      const count = exact ? opts.repeats : opts.min_repeats
      const scope = opts.scope ?? 'whole'
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5 text-[11px]">
		  <select
			  value={scope}
			  onChange={(e) => {
				  const nextScope = e.target.value as 'whole' | 'prefix' | 'suffix' | 'range'
				  onChange({
					  ...opts,
					  scope: nextScope,
					  segment_start: nextScope === 'range' ? numericOr(opts.segment_start, 1) : 1,
					  segment_length: nextScope === 'whole' ? 0 : numericOr(opts.segment_length, 6),
				  })
			  }}
			  className="h-8 rounded-md border border-input bg-background px-2 text-xs"
			  aria-label="重复结构作用范围"
		  >
			  <option value="whole">整个域名</option>
			  <option value="prefix">前几位</option>
			  <option value="suffix">后几位</option>
			  <option value="range">指定位置</option>
		  </select>
		  {scope === 'range' && (
			  <>
				  <span className="text-muted-foreground">从第</span>
				  <Input
					  type="number" min={1} max={63} value={opts.segment_start ?? 1}
					  onChange={(e) => onChange({ ...opts, segment_start: numberOrBlank(e.target.value) })}
					  className={cn(common, 'w-16')} aria-label="片段起始位置"
				  />
				  <span className="text-muted-foreground">位起，取</span>
			  </>
		  )}
		  {(scope === 'prefix' || scope === 'suffix') && (
			  <span className="text-muted-foreground">取</span>
		  )}
		  {scope !== 'whole' && (
			  <>
				  <Input
					  type="number" min={1} max={63} value={opts.segment_length ?? 6}
					  onChange={(e) => onChange({ ...opts, segment_length: numberOrBlank(e.target.value) })}
					  className={cn(common, 'w-16')} aria-label="片段长度"
				  />
				  <span className="text-muted-foreground">位</span>
			  </>
		  )}
          <span className="text-muted-foreground">重复长度</span>
          <Input
            type="number" min={0} max={63} value={num(opts.unit_length)}
            onChange={(e) => onChange({ ...opts, unit_length: numberOrBlank(e.target.value) })}
            className={cn(common, 'w-16')} placeholder="不限" aria-label="重复长度"
          />
          <select
            value={exact ? 'exact' : 'minimum'}
            onChange={(e) => {
			  const nextCount = Math.max(numericOr(opts.repeats, numericOr(opts.min_repeats, 2)), 1)
              onChange(e.target.value === 'exact'
                ? { ...opts, repeats: nextCount, min_repeats: 0 }
                : { ...opts, repeats: 0, min_repeats: nextCount })
            }}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            aria-label="重复次数模式"
          >
            <option value="exact">精确重复</option>
            <option value="minimum">至少重复</option>
          </select>
          <Input
            type="number" min={1} max={63} value={num(count)}
            onChange={(e) => {
			  const nextCount = numberOrBlank(e.target.value)
              onChange(exact
                ? { ...opts, repeats: nextCount, min_repeats: 0 }
                : { ...opts, repeats: 0, min_repeats: nextCount })
            }}
            className={cn(common, 'w-16')}
            placeholder="2+"
            aria-label={exact ? '重复次数' : '最少重复次数'}
          />
          <span className="text-muted-foreground">次</span>
          {/* Without this the dimensions describe two shapes at once: 重复长度 2
              × 3 次 is 636363 and 112233 alike, so a rule written for one also
              tags the other. */}
          <select
            value={opts.arrangement ?? 'any'}
            onChange={(e) => onChange({ ...opts, arrangement: e.target.value as 'any' | 'block' | 'grouped' })}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            aria-label="排列方式"
          >
            <option value="any">两种排列都要</option>
            <option value="block">单元内字符不同（ABABAB）</option>
            <option value="grouped">单元内字符相同（AABBCC）</option>
          </select>
          <Tooltip content="不选排列方式时，一组参数会同时匹配两种结构：重复长度 2、重复 3 次，既匹配 636363，也匹配 112233。">
            <span className="cursor-help text-muted-foreground">ⓘ</span>
          </Tooltip>
          <span className={cn(
            'basis-full pl-0.5 text-[10px]',
            count === 1 && numericOr(opts.unit_length, 0) < 2 ? 'text-destructive' : 'text-muted-foreground',
          )}>
            {repeatOptionsExample(opts)}
          </span>
        </span>
      )
    }

    case 'sequence_options': {
      const opts = (value ?? {}) as { direction?: string; min_length?: number | ''; step?: number | '' }
	  const minLength = opts.min_length ?? opts.step ?? 4
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <select
            value={opts.direction ?? 'any'}
            onChange={(e) => onChange({ ...opts, direction: e.target.value })}
            className="h-8 rounded-md border border-input bg-background px-2 text-xs"
            aria-label="方向"
          >
            <option value="any">任意方向</option>
            <option value="asc">递增</option>
            <option value="desc">递减</option>
          </select>
		  <span className="text-muted-foreground">连续至少</span>
		  <Input
			type="number" min={2} max={63}
			value={minLength}
			onChange={(e) => onChange({ direction: opts.direction ?? 'any', min_length: numberOrBlank(e.target.value) })}
			className={cn(common, 'w-16')} aria-label="连续位数"
		  />
		  <span className="text-muted-foreground">位</span>
        </span>
      )
    }

    case 'grouped_repeat_options': {
      const opts = (value ?? {}) as { group_size?: number | '' }
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground">每组连续重复</span>
          <Input
            type="number" min={1} max={63} value={opts.group_size ?? 2}
            onChange={(e) => onChange({ ...opts, group_size: numberOrBlank(e.target.value) })}
            aria-invalid={invalid}
            className={cn(common, 'w-16')} aria-label="每组重复次数"
          />
          <span className="text-muted-foreground">次</span>
          <span className="basis-full pl-0.5 text-[10px] text-muted-foreground">
            示例：aabbccdd、22334455；组数由域名长度自动决定，相邻组字符不同
          </span>
        </span>
      )
    }

    default:
      return null
  }
}

function TldMultiSelect({
	options, value, onChange, invalid,
}: {
	options: NonNullable<FieldSpec['enum']>
	value: string[]
	onChange: (value: string[]) => void
	invalid?: boolean
}) {
	const [open, setOpen] = React.useState(false)
	const rootRef = React.useRef<HTMLDivElement>(null)
	const selected = options.filter((option) => value.includes(option.value))

	React.useEffect(() => {
		if (!open) return

		const closeOnOutsideClick = (event: MouseEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
		}
		const closeOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false)
		}

		document.addEventListener('mousedown', closeOnOutsideClick)
		document.addEventListener('keydown', closeOnEscape)
		return () => {
			document.removeEventListener('mousedown', closeOnOutsideClick)
			document.removeEventListener('keydown', closeOnEscape)
		}
	}, [open])

	const toggleOption = (optionValue: string, checked: boolean) => {
		onChange(checked
			? value.filter((item) => item !== optionValue)
			: [...value, optionValue])
	}

	return (
		<div ref={rootRef} className="relative inline-block min-w-44">
			<button
				type="button"
				onClick={() => setOpen((current) => !current)}
				aria-expanded={open}
				aria-haspopup="listbox"
				aria-invalid={invalid}
				className={cn(
					'flex h-9 min-w-44 items-center rounded-md border bg-background px-3 text-left text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
					invalid ? 'border-destructive' : 'border-input',
				)}
			>
				<span className="min-w-0 flex-1 truncate">
					{selected.length === 0
						? '选择平台启用后缀…'
						: selected.length <= 2
							? selected.map((option) => option.label).join('、')
							: `已选择 ${selected.length} 个后缀`}
				</span>
				<ChevronDown className={cn(
					'ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform',
					open && 'rotate-180',
				)} aria-hidden />
			</button>
			{open && (
				<div className="absolute left-0 top-10 z-40 min-w-full rounded-md border bg-popover shadow-lg">
					<div role="listbox" aria-multiselectable="true" className="max-h-56 overflow-y-auto p-1">
						{options.map((option) => {
							const checked = value.includes(option.value)
							return (
								<button
									type="button"
									key={option.value}
									role="option"
									aria-selected={checked}
									onClick={() => toggleOption(option.value, checked)}
									className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-accent"
								>
									<Checkbox checked={checked} aria-label={`选择 ${option.label}`} />
									{option.label}
								</button>
							)
						})}
					</div>
					<div className="flex items-center justify-between border-t px-2 py-1.5">
						<span className="text-[11px] text-muted-foreground">已选 {selected.length} 项</span>
						<Button type="button" variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setOpen(false)}>
							完成
						</Button>
					</div>
				</div>
			)}
		</div>
	)
}

export function defaultValueFor(spec: FieldSpec | undefined, operator: string): unknown {
  const kind = spec?.operators[operator]
  const firstVisibleEnum = spec?.enum?.find((option) => !option.hidden)
  switch (kind) {
    case 'bool': return true
    case 'string': return firstVisibleEnum?.value ?? ''
    case 'string_list': return firstVisibleEnum ? [firstVisibleEnum.value] : []
    case 'int': case 'days': case 'float': return 0
    case 'int_range': return [1, 10]
    case 'time': return new Date().toISOString()
    case 'sequence_options': return { direction: 'any', min_length: 4 }
    case 'repeat_options': return {
		unit_length: 0, repeats: 0, min_repeats: 2,
		scope: 'whole', segment_start: 1, segment_length: 0,
	}
    case 'grouped_repeat_options': return { group_size: 2 }
    default: return ''
  }
}

/** Chooses a human-friendly operator instead of sorting internal codes alphabetically. */
export function preferredOperator(spec: FieldSpec | undefined): string {
  if (!spec) return 'EQ'
  const priorities = ['EQ', 'IN', 'IS', 'HAS', 'GTE', 'CONTAINS', 'BETWEEN']
  return priorities.find((operator) => operator in spec.operators)
    ?? Object.keys(spec.operators)[0]
    ?? 'EQ'
}

/** Returns errors on the node itself or any of its fields/array values. */
export function findNodeError(errors: Record<string, string>, path: string): string | undefined {
  const match = Object.entries(errors).find(([errorPath]) =>
    errorPath === path || errorPath.startsWith(`${path}.`) || errorPath.startsWith(`${path}[`),
  )
  return match?.[1]
}

export function operatorLabel(op: string): string {
  const map: Record<string, string> = {
    EQ: '等于', NEQ: '不等于', IN: '属于', NOT_IN: '不属于',
    LT: '小于', LTE: '小于等于', GT: '大于', GTE: '大于等于',
    BETWEEN: '介于', STARTS_WITH: '开头是', ENDS_WITH: '结尾是',
    CONTAINS: '包含', NOT_CONTAINS: '不包含', REGEX: '正则匹配',
    IS: '是', HAS: '具有', NOT_HAS: '不具有',
    BEFORE: '早于', AFTER: '晚于', WITHIN_DAYS: '未来 N 天内', IS_NULL: '为空',
  }
  return map[op] ?? op
}

function toLocalInput(value: unknown): string {
  if (typeof value !== 'string') return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function repeatOptionsExample(opts: {
	unit_length?: number | ''
	repeats?: number | ''
	min_repeats?: number | ''
	arrangement?: string
	scope?: string
	segment_length?: number | ''
}): string {
	const unitLength = numericOr(opts.unit_length, 0)
	const repeats = numericOr(opts.repeats, 0)
	const minRepeats = numericOr(opts.min_repeats, 0)
	const exact = repeats > 0
	const count = exact ? repeats : minRepeats
  if (count === 1 && unitLength < 2) return '重复 1 次时，重复长度至少为 2（表示一组相同字符）'
  const arrangement = opts.arrangement ?? 'any'
  if (unitLength <= 0 || count <= 0) {
    return arrangement === 'block' ? '例：重复长度 2 × 3 次，可匹配 ABABAB（如 636363）'
      : arrangement === 'grouped' ? '例：重复长度 2 × 3 次，可匹配 AABBCC（如 112233）'
      : '例：重复长度 2 × 3 次，可匹配 ABABAB 或 AABBCC'
  }
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
	if (count === 1) {
		return `可匹配：${'A'.repeat(unitLength)}（1 组，每组 ${unitLength} 个相同字符）`
	}
  const unit = alphabet.slice(0, Math.min(unitLength, alphabet.length)) || 'A'
  const shown = unit.repeat(Math.min(count, 4))
  const suffix = count > 4 || !exact ? '…' : ''
  const numericHint = unitLength === 1 ? '；纯数字规则下可匹配 888888' : ''
	const scope = opts.scope === 'prefix' ? `前 ${numericOr(opts.segment_length, 0)} 位` : opts.scope === 'suffix' ? `后 ${numericOr(opts.segment_length, 0)} 位` : opts.scope === 'range' ? `所选 ${numericOr(opts.segment_length, 0)} 位片段` : '整个域名'
	const grouped = Array.from({ length: Math.min(count, 4) }, (_, index) =>
		(alphabet[index] ?? 'X').repeat(unitLength),
	).join('')
	// One example per arrangement once one is chosen: promising both is how the
	// rule looked right and caught twice what was intended.
	if (arrangement === 'block') {
		return `${scope} · 只匹配：${shown}${suffix}（单元内字符不同）${numericHint}`
	}
	if (arrangement === 'grouped') {
		return `${scope} · 只匹配：${grouped}${suffix}（单元内字符相同）`
	}
	return `${scope} · 可匹配：${shown}${suffix}，也可匹配：${grouped}${suffix}${numericHint}`
}

function numberOrBlank(raw: string): number | '' {
  return raw === '' ? '' : Number(raw)
}

function numericOr(value: number | '' | undefined, fallback: number): number {
	return typeof value === 'number' ? value : fallback
}

/** Counts nodes so the editor can enforce the §6.6 ceiling. */
export function countNodes(node: RuleNode): number {
  if (!isGroup(node)) return 1
  return 1 + node.children.reduce((sum, c) => sum + countNodes(c), 0)
}

/** Computes the tree's maximum depth. */
export function treeDepth(node: RuleNode, depth = 1): number {
  if (!isGroup(node) || node.children.length === 0) return depth
  return Math.max(...node.children.map((c) => treeDepth(c, depth + 1)))
}
